import React, { useState } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { addFormSubmissionToBrevo } from '../services/brevoService';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // EmailJS configuration - UPDATED WITH YOUR ACTUAL VALUES
  const EMAILJS_SERVICE_ID = "service_q9czpsy"; // Your EmailJS Service ID
  const EMAILJS_TEMPLATE_ID = "template_vbc"; // You need to create this template in EmailJS
  const EMAILJS_PUBLIC_KEY = "CrUwO8SVJ5ZJulRFw"; // Your EmailJS Public Key

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Prepare email template parameters
      const templateParams = {
        to_email: 'sg@vbcollective.xyz',
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company || 'Not specified',
        interest: formData.interest,
        message: formData.message,
        reply_to: formData.email,
      };

      // Send email using EmailJS and add to Brevo simultaneously
      const [emailResult, brevoResult] = await Promise.allSettled([
        emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY),
        addFormSubmissionToBrevo(formData.email, formData.name, formData.company, formData.interest, formData.message)
      ]);
      
      // Check EmailJS result
      if (emailResult.status === 'fulfilled') {
        console.log('Email sent successfully via EmailJS');
      } else {
        console.warn('EmailJS failed:', emailResult.reason);
      }
      
      // Check Brevo result
      if (brevoResult.status === 'fulfilled') {
        console.log('Contact added to Brevo successfully');
      } else {
        console.warn('Brevo integration failed:', brevoResult.reason);
      }
      
      // Success if at least one method worked
      if (emailResult.status === 'fulfilled' || brevoResult.status === 'fulfilled') {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          company: '',
          interest: '',
          message: ''
        });
        
        console.log('Form submission processed successfully');
      } else {
        throw new Error('Both email delivery and contact management failed');
      }
      
    } catch (error) {
      console.error('Error processing form submission:', error);
      setError('Failed to send message. Please try again or contact us directly at info@vbcollective.xyz');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: '📧',
      title: 'Email Us',
      content: 'info@vbcollective.xyz',
      description: 'Get in touch for investments, partnerships and inquiries'
    }
  ];

  const interestOptions = [
    'Investment Opportunities',
    'Venture Building',
    'Advisory Services',
    'Partnership',
    'Vacancy',
    'General Inquiry'
  ];

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-navy-950 to-navy-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-5"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1495576775051-8af0d10f19b1')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-navy-950/90 to-navy-900/80" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-display font-bold text-white mb-6">
            Get in <span className="gradient-text">Touch</span>
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            Ready to start your journey with VBC? Let's discuss how we can help 
            transform your vision into reality.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 border border-white/10"
          >
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
                    <p className="text-red-400 text-sm">{error}</p>
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400 transition-all duration-300"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400 transition-all duration-300"
                      placeholder="john@company.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400 transition-all duration-300"
                      placeholder="Your Company"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Interest Area *
                    </label>
                    <select
                      name="interest"
                      required
                      value={formData.interest}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400 transition-all duration-300"
                    >
                      <option value="">Select an option</option>
                      {interestOptions.map((option) => (
                        <option key={option} value={option} className="bg-navy-800">
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400 transition-all duration-300 resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </motion.button>
              </form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-12"
              >
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-green-400 text-2xl">✓</span>
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">
                  Message Sent Successfully!
                </h3>
                <p className="text-gray-300 mb-6">
                  Thank you for reaching out. We'll get back to you within 24 hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary"
                >
                  Send Another Message
                </button>
              </motion.div>
            )}
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Let's Start a Conversation
              </h3>
              <p className="text-gray-300 leading-relaxed mb-8">
                Whether you're an entrepreneur with a groundbreaking idea looking to scale or to raise capital, an investor 
                looking for opportunities to co-invest with tier 1 investors, or a company seeking strategic guidance, 
                we're here to help you succeed.
              </p>
            </div>

            {/* Contact Info Cards */}
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="glass rounded-xl p-6 border border-white/10 hover:border-secondary-400/30 transition-all duration-300 group"
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-2xl group-hover:scale-110 transition-transform duration-300">
                      {info.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-secondary-400 transition-colors duration-300">
                        {info.title}
                      </h4>
                      <p className="text-secondary-400 font-medium mb-1">
                        {info.content}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Follow Us Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="glass rounded-xl p-6 border border-white/10 hover:border-secondary-400/30 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-secondary-400 transition-colors duration-300">
                      Follow Us
                    </h4>
                    <p className="text-gray-400 text-sm">
                      Stay connected with our latest updates
                    </p>
                  </div>
                  <a
                    href="https://www.linkedin.com/company/vbcollective/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center text-gray-400 hover:text-secondary-400 hover:bg-secondary-400/10 transition-all duration-300 border border-white/10 hover:border-secondary-400/30 group-hover:scale-110"
                    aria-label="LinkedIn"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </a>
                </div>
              </motion.div>

              {/* Book a Meeting Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
                className="glass rounded-xl p-6 border border-white/10 hover:border-secondary-400/30 transition-all duration-300 group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-secondary-400 transition-colors duration-300">
                      Book a Meeting to Explore Collaboration
                    </h4>
                    <p className="text-gray-400 text-sm">
                      Schedule a 30-minute consultation with our team
                    </p>
                  </div>
                  <a
                    href="https://calendly.com/vbcollective/30min"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 bg-white/5 rounded-lg flex items-center justify-center text-gray-400 hover:text-secondary-400 hover:bg-secondary-400/10 transition-all duration-300 border border-white/10 hover:border-secondary-400/30 group-hover:scale-110"
                    aria-label="Book a Meeting"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                    </svg>
                  </a>
                </div>
              </motion.div>

              {/* Want to Work for Us Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="glass rounded-xl p-6 border border-white/10 hover:border-secondary-400/30 transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="text-2xl group-hover:scale-110 transition-transform duration-300">
                    💼
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-2 group-hover:text-secondary-400 transition-colors duration-300">
                      Want to Work for Us
                    </h4>
                    <p className="text-secondary-400 font-medium mb-1">
                      hireme@vbcollective.xyz
                    </p>
                    <p className="text-gray-400 text-sm">
                      Email us with your CV and a cover letter
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;